import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getSettingsSection, saveSettingsSection } from '../../../../services/settingsService';
import {
  FaShieldAlt, FaLock, FaMobile, FaHistory, FaExclamationTriangle,
  FaCheckCircle, FaClock, FaKey, FaUserShield
} from 'react-icons/fa';

const PLANNED_FEATURES = [
  { icon: <FaMobile />, title: 'Two-Factor Authentication (2FA)', description: 'Require staff and admins to verify identity via SMS or authenticator app on every login.', status: 'coming-soon' },
  { icon: <FaHistory />, title: 'Session Management', description: 'View active sessions across devices, set auto-logout timers, and remotely revoke access.', status: 'coming-soon' },
  { icon: <FaUserShield />, title: 'Single Sign-On (SSO)', description: 'Integrate with Microsoft Azure AD or Google Workspace for centralised identity management.', status: 'roadmap' },
  { icon: <FaKey />, title: 'API Key Management', description: 'Generate and revoke API keys for HydraJaws and third-party integrations.', status: 'coming-soon' },
  { icon: <FaLock />, title: 'IP Allowlist', description: 'Restrict platform access to approved IP addresses or CIDR ranges for maximum security.', status: 'roadmap' },
  { icon: <FaHistory />, title: 'Audit Log Export', description: 'Export a tamper-proof PDF or CSV of all platform activity for compliance reporting.', status: 'coming-soon' },
];

function SecuritySettings() {
  const [security, setSecurity] = useState({
    passwordPolicy: 'standard',
    twoFactorEnabled: false,
    backupEmail: '',
    sessionTimeout: '8h',
  });
  const [saving, setSaving] = useState(false);

  const passwordPolicies = [
    { value: 'standard', label: 'Standard \u2014 8+ characters' },
    { value: 'strong', label: 'Strong \u2014 12+ characters, special characters required' },
    { value: 'custom', label: 'Custom Policy' },
  ];

  const sessionTimeouts = [
    { value: '1h', label: '1 hour' },
    { value: '4h', label: '4 hours' },
    { value: '8h', label: '8 hours (recommended)' },
    { value: '24h', label: '24 hours' },
    { value: 'never', label: 'Never (not recommended)' },
  ];

  useEffect(() => {
    getSettingsSection('security')
      .then((data) => {
        if (data && Object.keys(data).length > 0) {
          setSecurity((prev) => ({ ...prev, ...data }));
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSecurity(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validateEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (security.backupEmail && !validateEmail(security.backupEmail)) {
      toast.error('Please enter a valid backup email address', { autoClose: 4000 });
      return;
    }
    setSaving(true);
    try {
      await saveSettingsSection('security', security);
      toast.success('Security settings saved', { autoClose: 3000 });
    } catch (error) {
      toast.error(error.message || 'Failed to save security settings', { autoClose: 4000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="stg-page-header-row">
        <div className="stg-page-icon stg-page-icon-blue"><FaShieldAlt /></div>
        <div>
          <h1 className="stg-page-title">Security Settings</h1>
          <p className="stg-page-subtitle">Control password policies, session behaviour, and authentication options.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="stg-section stg-max-form">
          <h2 className="stg-section-title">Active Configuration</h2>

          <div className="stg-form-group">
            <label htmlFor="passwordPolicy" className="stg-form-label">Password Policy</label>
            <select id="passwordPolicy" name="passwordPolicy" className="stg-select" value={security.passwordPolicy} onChange={handleChange}>
              {passwordPolicies.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          <div className="stg-form-group">
            <label htmlFor="sessionTimeout" className="stg-form-label">Auto Session Timeout</label>
            <select id="sessionTimeout" name="sessionTimeout" className="stg-select" value={security.sessionTimeout} onChange={handleChange}>
              {sessionTimeouts.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <p className="stg-help-text">Users will be automatically signed out after this period of inactivity.</p>
          </div>

          <div className="stg-form-group">
            <label className="stg-toggle-row">
              <input type="checkbox" name="twoFactorEnabled" className="stg-toggle-checkbox" checked={security.twoFactorEnabled} onChange={handleChange} />
              <div className="stg-toggle-body">
                <span className="stg-toggle-label">Enable Two-Factor Authentication (2FA)</span>
                <span className="stg-toggle-help">
                  {security.twoFactorEnabled
                    ? '2FA is enabled \u2014 users will be prompted on next login.'
                    : 'Full 2FA integration is coming soon. Enabling this flag now will activate it when ready.'}
                </span>
              </div>
            </label>
          </div>

          <div className="stg-form-group">
            <label htmlFor="backupEmail" className="stg-form-label">Backup / Recovery Email</label>
            <input type="email" id="backupEmail" name="backupEmail" className="stg-input" value={security.backupEmail} onChange={handleChange} placeholder="Optional \u2014 used for account recovery" />
          </div>

          <button type="submit" className="stg-btn stg-btn-primary" disabled={saving}>
            {saving ? 'Saving\u2026' : 'Save Changes'}
          </button>
        </div>
      </form>

      <div className="stg-section">
        <h2 className="stg-section-title">Planned Security Features</h2>
        <p className="stg-section-desc">The following security capabilities are in development and will be available in upcoming releases.</p>
        <div className="stg-planned-grid">
          {PLANNED_FEATURES.map((f, i) => (
            <div key={i} className={`stg-planned-card ${f.status === 'coming-soon' ? 'stg-planned-blue' : 'stg-planned-gray'}`}>
              <div className="stg-planned-icon">{f.icon}</div>
              <div className="stg-planned-body">
                <div className="stg-planned-header">
                  <strong>{f.title}</strong>
                  <span className={`stg-pill ${f.status === 'coming-soon' ? 'stg-pill-blue' : 'stg-pill-gray'}`}>
                    {f.status === 'coming-soon' ? 'Coming Soon' : 'On Roadmap'}
                  </span>
                </div>
                <p>{f.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="stg-status-card stg-max-form">
        <div className="stg-status-header"><FaExclamationTriangle /> Current Security Status</div>
        <div className="stg-status-list">
          {[
            { ok: true, label: 'Password policy active', detail: security.passwordPolicy },
            { ok: true, label: 'JWT token authentication', detail: 'Bearer tokens + role-based routing' },
            { ok: false, label: '2FA not yet active', detail: 'Full integration coming soon' },
            { ok: true, label: 'HTTPS enforced', detail: 'All traffic encrypted in transit' },
            { ok: false, label: 'Session refresh tokens pending', detail: 'Planned for next release' },
          ].map((s, i) => (
            <div key={i} className="stg-status-row">
              <span className={`stg-status-icon ${s.ok ? 'stg-status-ok' : 'stg-status-warn'}`}>
                {s.ok ? <FaCheckCircle /> : <FaClock />}
              </span>
              <span className="stg-status-label">{s.label}</span>
              <span className="stg-status-detail">{s.detail}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SecuritySettings;
